import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { BenchmarkCard } from "@/components/BenchmarkCard";
import { PortalBalanceCard } from "@/components/PortalBalanceCard";
import { PageHeader } from "@/components/PageHeader";
import { PlatformBadge } from "@/components/PlatformLogo";
import { SectionHeader } from "@/components/SectionHeader";
import { Skeleton } from "@/components/Skeleton";
import { presenceLabel } from "@/lib/presence";
import type {
  BrokerBenchmarks,
  Placement,
  TeamMember,
  TeamSession,
  UserRole,
  UserStatus,
} from "@/types";
import { roleLabel } from "@/types";

const ROLE_STYLES: Record<UserRole, string> = {
  shareholder: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  executive_manager: "border-accent-500/30 bg-accent-500/10 text-accent-400",
  operations_manager: "border-accent-500/25 bg-accent-500/[0.06] text-accent-300",
  lead_broker: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  broker: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  member: "border-slate-500/30 bg-slate-500/10 text-slate-300",
};

const STATUS_STYLES: Record<UserStatus, string> = {
  active: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  disabled: "border-rose-500/40 bg-rose-500/15 text-rose-200",
};

function formatDate(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function shortUA(ua: string | null): string {
  if (!ua) return "—";
  // Extract the browser + OS in a compact way. Full UA strings are too noisy.
  const browser =
    /Chrome\/\d+/.exec(ua)?.[0] ||
    /Firefox\/\d+/.exec(ua)?.[0] ||
    /Safari\/\d+/.exec(ua)?.[0] ||
    /Edg\/\d+/.exec(ua)?.[0] ||
    null;
  const os =
    /(Mac OS X [\d_]+)/.exec(ua)?.[0]?.replace(/_/g, ".") ||
    /(Windows NT [\d.]+)/.exec(ua)?.[0] ||
    /(Android \d+)/.exec(ua)?.[0] ||
    /(iPhone|iPad|Linux x86_64|Linux)/.exec(ua)?.[0] ||
    null;
  return [browser, os].filter(Boolean).join(" · ") || ua.slice(0, 60);
}

interface PlacementList {
  items: Placement[];
  total: number;
  limit: number;
  offset: number;
}

export function EmployeeDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { user: viewer } = useAuth();
  const navigate = useNavigate();

  const [member, setMember] = useState<TeamMember | null>(null);
  const [sessions, setSessions] = useState<TeamSession[]>([]);
  const [placements, setPlacements] = useState<Placement[]>([]);
  const [placementTotal, setPlacementTotal] = useState(0);
  const [benchmarks, setBenchmarks] = useState<BrokerBenchmarks | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  // Edit name inline.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSaving, setNameSaving] = useState(false);

  // Reveal password flash card after a reset.
  const [pwBusy, setPwBusy] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  // Owners/directors can reassign a member's manager — load the pool of
  // possible managers (owner + director + manager roles, minus the
  // member themselves) lazily so non-admin viewers don't fan out an
  // unnecessary list call. Same agency only; ``/api/team`` already
  // scopes that by viewer.
  const [eligibleManagers, setEligibleManagers] = useState<TeamMember[] | null>(
    null,
  );

  async function loadMember() {
    if (!userId) return;
    try {
      const m = await api<TeamMember>(`/api/team/${userId}`);
      setMember(m);
      setNameDraft(m.full_name || "");
    } catch (err) {
      setLoadErr(err instanceof Error ? err.message : "Failed to load employee");
    }
  }

  async function loadSessions() {
    if (!userId) return;
    try {
      const rows = await api<TeamSession[]>(`/api/team/${userId}/sessions?limit=20`);
      setSessions(rows);
    } catch {
      setSessions([]);
    }
  }

  async function loadPlacements() {
    if (!userId) return;
    try {
      const data = await api<PlacementList>(
        `/api/placements?user_id=${userId}&limit=10`,
      );
      setPlacements(data.items);
      setPlacementTotal(data.total);
    } catch {
      setPlacements([]);
    }
  }

  async function loadBenchmarks() {
    if (!userId) return;
    try {
      const data = await api<BrokerBenchmarks>(
        `/api/team/${userId}/benchmarks`,
      );
      setBenchmarks(data);
    } catch {
      setBenchmarks(null);
    }
  }

  useEffect(() => {
    loadMember();
    loadSessions();
    loadPlacements();
    loadBenchmarks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const canManage = useMemo(() => {
    if (!viewer || !member) return false;
    // Shareholder immunity: only a shareholder can touch a shareholder
    // (defensive — the backend hides shareholders from everyone else).
    if (member.role === "shareholder") return viewer.role === "shareholder";
    if (viewer.role === "shareholder") return true; // manages everyone
    if (viewer.role === "executive_manager") return member.role !== "executive_manager" || member.id === viewer.id;
    if (viewer.role === "operations_manager") {
      // Director matches owner-mgmt scope except they can't touch the
      // owner. Self-mutation stays allowed for profile fields.
      return member.role !== "executive_manager" || member.id === viewer.id;
    }
    if (viewer.role === "lead_broker") {
      return member.manager_id === viewer.id || member.id === viewer.id;
    }
    return false;
  }, [viewer, member]);

  const canResetPassword = canManage && viewer?.id !== member?.id;
  // A shareholder viewer may change role / status / manager of anyone
  // (including executives); exec + director keep their existing scope but
  // can never touch a shareholder row.
  const isShareholderViewer = viewer?.role === "shareholder";
  const memberIsApex =
    member?.role === "executive_manager" || member?.role === "shareholder";
  const canElevatedManage =
    isShareholderViewer ||
    ((viewer?.role === "executive_manager" || viewer?.role === "operations_manager") &&
      !memberIsApex);
  const canChangeRole = canElevatedManage;
  const canToggleStatus = canElevatedManage && member?.id !== viewer?.id;
  // Same gate the backend uses on PATCH /team/{id} for manager_id.
  const canChangeManager = canElevatedManage;

  // Lazy-load the eligible-manager pool — only when the viewer is
  // allowed to reassign managers AND the member is one whose manager
  // we can change. Skip otherwise so brokers / managers viewing their
  // own page don't pay for a list call they can't act on.
  useEffect(() => {
    if (!canChangeManager) return;
    if (eligibleManagers !== null) return;
    (async () => {
      try {
        const all = await api<TeamMember[]>("/api/team");
        // Eligible = owner, director, or manager — and never the
        // member themselves (the backend rejects cannot_manage_self).
        const pool = all.filter(
          (m) =>
            m.id !== member?.id &&
            (m.role === "executive_manager" ||
              m.role === "operations_manager" ||
              m.role === "lead_broker"),
        );
        setEligibleManagers(pool);
      } catch {
        // Non-fatal — the select just shows the current value.
        setEligibleManagers([]);
      }
    })();
  }, [canChangeManager, eligibleManagers, member?.id]);

  async function saveName() {
    if (!member) return;
    setNameSaving(true);
    setActionErr(null);
    try {
      const m = await api<TeamMember>(`/api/team/${member.id}`, {
        method: "PATCH",
        json: { full_name: nameDraft.trim() || null },
      });
      setMember(m);
      setEditingName(false);
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : "Failed to save");
    } finally {
      setNameSaving(false);
    }
  }

  async function resetPassword() {
    if (!member) return;
    if (
      !confirm(
        `Reset password for ${member.full_name || member.email}? Every existing session will be logged out immediately, and a one-time password will be generated for you to share.`,
      )
    )
      return;
    setPwBusy(true);
    setActionErr(null);
    try {
      const r = await api<{ new_password: string }>(
        `/api/team/${member.id}/reset-password`,
        { method: "POST" },
      );
      setNewPassword(r.new_password);
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : "Failed to reset password");
    } finally {
      setPwBusy(false);
    }
  }

  async function patchMember(body: Record<string, unknown>) {
    if (!member) return;
    setActionErr(null);
    try {
      const m = await api<TeamMember>(`/api/team/${member.id}`, {
        method: "PATCH",
        json: body,
      });
      setMember(m);
    } catch (err) {
      setActionErr(err instanceof ApiError ? err.message : "Failed to update");
    }
  }

  async function toggleStatus() {
    if (!member) return;
    const next = member.status === "active" ? "disabled" : "active";
    if (
      next === "disabled" &&
      !confirm("Deactivate this user? They will immediately be logged out.")
    )
      return;
    if (next === "disabled") {
      // Use the DELETE endpoint so report reassignment logic runs.
      try {
        const m = await api<TeamMember>(`/api/team/${member.id}`, { method: "DELETE" });
        setMember(m);
      } catch (err) {
        setActionErr(err instanceof ApiError ? err.message : "Failed to deactivate");
      }
      return;
    }
    patchMember({ status: "active" });
  }

  if (loadErr) {
    return (
      <div className="w-full">
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {loadErr} —{" "}
          <button onClick={() => navigate("/team")} className="underline">
            back to Team
          </button>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="w-full space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title={member.full_name || member.email}
        subtitle={
          <Link to="/team" className="text-accent-400 hover:text-accent-300">
            ← back to Team
          </Link>
        }
      />

      {actionErr && (
        <div className="card p-3 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {actionErr}
        </div>
      )}

      {/* Hero card — identity block on top, evenly-spaced metadata
          strip below. Same two-section pattern the Profile page uses
          so the two surfaces read as siblings of the same family. */}
      <div className="card overflow-hidden">
        <div className="px-5 py-5 flex flex-col sm:flex-row sm:items-center gap-5">
          <Avatar
            url={member.avatar_url}
            name={member.full_name}
            email={member.email}
            size="lg"
            status={member.presence_status}
            userId={member.id}
            className="mx-auto sm:mx-0"
          />
          <div className="flex-1 min-w-0 text-center sm:text-left">
            {editingName && canManage ? (
              <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                <input
                  className="bg-ink-850 border border-white/15 rounded-md py-1 px-2 text-[17px] font-semibold text-slate-100 w-full sm:w-64 focus:outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/20"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  disabled={nameSaving}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveName();
                    if (e.key === "Escape") {
                      setNameDraft(member.full_name || "");
                      setEditingName(false);
                    }
                  }}
                  placeholder="Full name"
                />
                <button
                  onClick={saveName}
                  disabled={nameSaving}
                  className="text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-accent-600 hover:bg-accent-500 text-white transition-colors disabled:opacity-50"
                >
                  {nameSaving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => {
                    setNameDraft(member.full_name || "");
                    setEditingName(false);
                  }}
                  disabled={nameSaving}
                  className="text-[11.5px] text-slate-400 hover:text-slate-200 px-2 py-1"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                <h2 className="text-[18px] font-semibold text-slate-100 leading-tight">
                  {member.full_name || (
                    <span className="text-slate-500">— no name set —</span>
                  )}
                </h2>
                {canManage && (
                  <button
                    onClick={() => setEditingName(true)}
                    className="text-[10px] text-slate-600 hover:text-accent-300 transition"
                    title="Edit name"
                  >
                    ✎
                  </button>
                )}
                <span className={`badge ${ROLE_STYLES[member.role]}`}>
                  {roleLabel(member.role)}
                </span>
                <span className={`badge ${STATUS_STYLES[member.status]}`}>
                  {member.status}
                </span>
              </div>
            )}
            <div className="mt-1.5 text-[12.5px] text-slate-400 font-mono truncate">
              {member.email}
            </div>
            {(() => {
              const pl = presenceLabel(
                member.presence_status,
                member.last_active_at,
                member.last_seen_at,
              );
              if (!pl) return null;
              const tone =
                pl.tone === "active"
                  ? "text-emerald-400"
                  : pl.tone === "away"
                  ? "text-amber-400"
                  : "text-slate-500";
              const dot =
                pl.tone === "active"
                  ? "bg-emerald-400"
                  : pl.tone === "away"
                  ? "bg-amber-400"
                  : "";
              return (
                <div
                  className={`text-[11px] mt-2 inline-flex items-center gap-1.5 ${tone}`}
                >
                  {dot && (
                    <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                  )}
                  {pl.text}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Account metadata strip — five aligned tiles, same rhythm as
            the Profile hero card. */}
        <div className="px-5 py-4 border-t border-white/5 grid grid-cols-2 sm:grid-cols-5 gap-x-5 gap-y-3">
          <Stat label="Reports to" value={member.manager_name || "—"} />
          <Stat label="Joined" value={formatDate(member.created_at)} />
          <Stat label="Last login" value={formatDate(member.last_login_at)} />
          <Stat
            label="Placements"
            value={member.placement_count.toLocaleString()}
            mono
          />
          <Stat label="Status" value={member.status} />
        </div>
      </div>

      {/* Benchmark scorecard — placements/day + closed deals/month
          with a current-month bar chart and days-hit-goal rollup.
          Same component the broker sees on their own Profile, so the
          manager+ view stays in sync with the broker's. */}
      <BenchmarkCard stats={benchmarks} />

      {/* Live MyHome + SS wallet balances for this agent, so a manager can
          spot an empty wallet before publishing silently fails. */}
      <PortalBalanceCard userId={userId!} />

      {/* Admin actions + persona — side-by-side at xl+ so the page
          balances on widescreen instead of stacking into a tower. Both
          fall back to single-column at smaller widths. The wrapper
          renders nothing when this viewer has neither admin powers nor
          persona-edit rights for this user. */}
      {(canManage ||
        canChangeRole ||
        canToggleStatus ||
        canChangeManager) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Admin actions */}
          <div className="card overflow-hidden flex flex-col">
            <SectionHeader
              title="Admin actions"
              subtitle="Role, reporting line, and access controls for this user."
            />
            <div className="px-5 py-4 space-y-4 flex-1">
              {canChangeRole && (
                <AdminRow
                  label="Role"
                  hint="Managers can see and manage their reports."
                >
                  <select
                    className="w-full bg-ink-850 border border-white/10 rounded-md py-1.5 px-2 text-[12.5px] text-slate-100 focus:outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/20"
                    value={member.role}
                    onChange={(e) => patchMember({ role: e.target.value })}
                  >
                    <option value="member">Member</option>
                    <option value="broker">Broker · can see Hot Offers</option>
                    <option value="lead_broker">Lead Broker</option>
                    {/* Only the owner (or a shareholder) may promote someone
                        to operations manager — matches the backend whitelist. */}
                    {(viewer?.role === "executive_manager" || isShareholderViewer) && (
                      <option value="operations_manager">Operations Manager · Executive Manager's right hand</option>
                    )}
                    {/* Only a shareholder may grant the executive or
                        shareholder tiers. */}
                    {isShareholderViewer && (
                      <>
                        <option value="executive_manager">Executive Manager · agency principal</option>
                        <option value="shareholder">Shareholder · shareholder tier</option>
                      </>
                    )}
                  </select>
                </AdminRow>
              )}

              {canChangeManager && (
                <AdminRow
                  label="Managers"
                  hint={
                    member.managers.length
                      ? `Seen by ${member.managers.length} manager${member.managers.length === 1 ? "" : "s"}. Anyone ticked can view + edit this user's placements.`
                      : "No managers — only owners/directors can see this user's placements."
                  }
                >
                  {eligibleManagers === null ? (
                    <div className="text-[12px] text-slate-500">Loading…</div>
                  ) : eligibleManagers.length === 0 ? (
                    <div className="text-[12px] text-slate-500">
                      No eligible managers yet.
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-56 overflow-y-auto -mr-1 pr-1">
                      {eligibleManagers.map((m) => {
                        const selected = member.managers.some(
                          (x) => x.id === m.id,
                        );
                        return (
                          <label
                            key={m.id}
                            className={`flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer border text-[12.5px] transition ${
                              selected
                                ? "border-accent-500/40 bg-accent-500/[0.06] text-slate-100"
                                : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-accent-500"
                              checked={selected}
                              onChange={() => {
                                const next = selected
                                  ? member.managers
                                      .map((x) => x.id)
                                      .filter((id) => id !== m.id)
                                  : [...member.managers.map((x) => x.id), m.id];
                                patchMember({ manager_ids: next });
                              }}
                            />
                            <span className="truncate flex-1">
                              {m.full_name || m.email}
                            </span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-500">
                              {roleLabel(m.role)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </AdminRow>
              )}

              {(canResetPassword || canToggleStatus) && (
                <div className="pt-3 border-t border-white/5 flex flex-wrap gap-2">
                  {canResetPassword && (
                    <button
                      onClick={resetPassword}
                      disabled={pwBusy}
                      className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-200 transition disabled:opacity-50"
                    >
                      <span>🔑</span>
                      {pwBusy ? "Resetting…" : "Reset password"}
                    </button>
                  )}
                  {canToggleStatus && (
                    <button
                      onClick={toggleStatus}
                      className={
                        "text-[12px] font-medium px-3 py-1.5 rounded-md border transition " +
                        (member.status === "active"
                          ? "bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 border-rose-500/30"
                          : "bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 border-emerald-500/30")
                      }
                    >
                      {member.status === "active" ? "Deactivate" : "Reactivate"}
                    </button>
                  )}
                </div>
              )}

              {newPassword && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-1">
                    One-time password
                  </div>
                  <div className="text-[11.5px] text-slate-400 mb-2 leading-snug">
                    Share this with {member.full_name || member.email} securely. It
                    won't be shown again. Every prior session has been logged out.
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 font-mono text-[12px] bg-black/40 rounded px-3 py-2 text-amber-200 select-all break-all">
                      {newPassword}
                    </code>
                    <div className="flex flex-col gap-1.5">
                      <button
                        className="text-[11px] font-medium px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-200 transition"
                        onClick={() => navigator.clipboard.writeText(newPassword)}
                      >
                        Copy
                      </button>
                      <button
                        className="text-[11px] font-medium px-2.5 py-1 rounded-md text-slate-500 hover:text-slate-300"
                        onClick={() => setNewPassword(null)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Publish persona — voice / rules fed into the bulk-publish
              Gemini prompt. Renders an empty placeholder card when the
              viewer doesn't have edit rights, so the xl-grid columns
              stay balanced. */}
          {canManage ? (
            <PersonaCard member={member} onSaved={setMember} />
          ) : (
            <div className="hidden xl:block" />
          )}
        </div>
      )}

      {/* Per-user page access — exceptions on top of the role matrix, so a
          single broker can be handed Competition bulk (or have Finance taken
          away) without promoting them or re-opening the page to their whole
          role. Only shown to viewers who administer access at all, and never
          for apex roles, which see everything by construction. */}
      {viewer?.allowed_pages?.includes("/role-permissions") && !memberIsApex && (
        <PageAccessCard
          member={member}
          canEdit={canElevatedManage && member.id !== viewer.id}
        />
      )}

      {/* Recent login sessions */}
      <div className="card overflow-hidden">
        <SectionHeader
          title="Recent logins"
          subtitle={
            sessions.length === 0
              ? "No login history yet."
              : `Last ${sessions.length} session${sessions.length === 1 ? "" : "s"} — IP, device, and when they started.`
          }
        />
        {sessions.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/[0.015] border-b border-white/5">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium">IP</th>
                  <th className="text-left px-5 py-2.5 font-medium">Device</th>
                  <th className="text-left px-5 py-2.5 font-medium">Started</th>
                  <th className="text-left px-5 py-2.5 font-medium">Expires</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-2.5 text-slate-300 font-mono tabular-nums">
                      {s.ip || "—"}
                      {s.current && (
                        <span className="ml-2 badge border-accent-500/30 bg-accent-500/10 text-accent-300 text-[10px]">
                          this device
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-slate-400">{shortUA(s.user_agent)}</td>
                    <td className="px-5 py-2.5 text-slate-500 text-[11px]">
                      {formatDate(s.created_at)}
                    </td>
                    <td className="px-5 py-2.5 text-slate-500 text-[11px]">
                      {formatDate(s.expires_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Placements by this employee */}
      <div className="card overflow-hidden">
        <SectionHeader
          title="Placements"
          subtitle={
            placementTotal === 0
              ? "No placements yet."
              : `${placementTotal} total · showing the 10 most recent.`
          }
          right={
            placementTotal > 0 ? (
              <Link
                to={`/placements?user=${member.id}`}
                className="text-[12px] font-medium text-accent-400 hover:text-accent-300 whitespace-nowrap"
              >
                View all →
              </Link>
            ) : null
          }
        />
        {placements.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/[0.015] border-b border-white/5">
                <tr>
                  <th className="text-left px-5 py-2.5 font-medium">Platform</th>
                  <th className="text-left px-5 py-2.5 font-medium">Listing ID</th>
                  <th className="text-left px-5 py-2.5 font-medium">District</th>
                  <th className="text-right px-5 py-2.5 font-medium">Price</th>
                  <th className="text-left px-5 py-2.5 font-medium">Published</th>
                  <th className="text-right px-5 py-2.5 font-medium">Link</th>
                </tr>
              </thead>
              <tbody>
                {placements.map((p) => (
                  <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-5 py-2.5">
                      <PlatformBadge platform={p.platform} />
                    </td>
                    <td className="px-5 py-2.5 font-mono tabular-nums text-slate-200">
                      {p.external_id || "—"}
                    </td>
                    <td className="px-5 py-2.5 text-slate-400">{p.district || "—"}</td>
                    <td className="px-5 py-2.5 text-slate-200 tabular-nums text-right">
                      {p.price
                        ? `${p.currency === "USD" ? "$" : p.currency === "GEL" ? "₾" : p.currency ? p.currency + " " : ""}${Number(p.price).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : "—"}
                    </td>
                    <td className="px-5 py-2.5 text-slate-500 text-[11px]">
                      {formatDate(p.published_at)}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      <a
                        href={p.published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11.5px] font-medium text-accent-400 hover:text-accent-300"
                      >
                        Open ↗
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/** One row of the admin-actions card. Same horizontal rhythm as the
 *  inspector rows on the Deals modal — narrow label, the editable
 *  control beside it, optional hint underneath. */
function AdminRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[88px_1fr] items-start gap-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold pt-2 leading-snug">
        {label}
      </div>
      <div className="min-w-0">
        {children}
        {hint && (
          <div className="text-[11px] text-slate-500 mt-1 leading-snug">
            {hint}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label}
      </div>
      <div
        className={
          "text-[12.5px] text-slate-200 truncate" +
          (mono ? " tabular-nums font-semibold" : "")
        }
      >
        {value}
      </div>
    </div>
  );
}

interface PersonaCardProps {
  member: TeamMember;
  onSaved: (m: TeamMember) => void;
}

function PersonaCard({ member, onSaved }: PersonaCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(member.publish_persona ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // When the member rerenders (e.g. after a sibling save), keep the draft
  // in sync with the new persona unless we're mid-edit.
  useEffect(() => {
    if (!editing) setDraft(member.publish_persona ?? "");
  }, [editing, member.publish_persona]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      const m = await api<TeamMember>(`/api/team/${member.id}`, {
        method: "PATCH",
        json: { publish_persona: draft.trim() },
      });
      onSaved(m);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to save persona");
    } finally {
      setSaving(false);
    }
  }

  const persona = member.publish_persona?.trim() ?? "";
  return (
    <div className="card overflow-hidden flex flex-col">
      <SectionHeader
        title="Publish persona"
        subtitle="Voice and rules used by Bulk publish — Gemini writes each listing's description in this style. Leave empty for a neutral professional tone."
        right={
          !editing ? (
            <button
              type="button"
              className="text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-200 transition"
              onClick={() => setEditing(true)}
            >
              {persona ? "Edit" : "Set persona"}
            </button>
          ) : null
        }
      />
      <div className="px-5 py-4 flex-1 space-y-3">
        {editing ? (
          <>
            <textarea
              className="input min-h-[140px] text-sm"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                "e.g. Specializes in Vake / Saburtalo apartments. Formal " +
                "tone, lead with location and natural light, end with a " +
                "viewing invitation. Avoid marketing buzzwords."
              }
              maxLength={2000}
            />
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-500 tabular-nums">
                {draft.length} / 2000
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-[11.5px] text-slate-400 hover:text-slate-200 px-2 py-1"
                  onClick={() => {
                    setDraft(member.publish_persona ?? "");
                    setEditing(false);
                    setErr(null);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-accent-600 hover:bg-accent-500 text-white transition-colors disabled:opacity-50"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
            {err && (
              <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
                {err}
              </div>
            )}
          </>
        ) : persona ? (
          <pre className="whitespace-pre-wrap text-[12.5px] text-slate-300 bg-white/[0.02] rounded-md p-3 border border-white/5 font-sans leading-relaxed">
            {persona}
          </pre>
        ) : (
          <div className="text-[12.5px] text-slate-500 italic">
            No persona set — bulk-publish descriptions written for this agent
            will use a neutral professional voice.
          </div>
        )}
      </div>
    </div>
  );
}

interface UserPageDef {
  key: string;
  label: string;
  lockable: boolean;
  /** True = this user's ROLE already grants the page. The exception the
   *  card stores is measured against this baseline. */
  from_role: boolean;
}

interface UserPagesResponse {
  user_id: string;
  role: string;
  editable: boolean;
  pages: UserPageDef[];
  grants: string[];
  denials: string[];
  effective: string[];
}

interface PageAccessCardProps {
  member: TeamMember;
  /** False ⇒ render read-only. The GET is open to anyone holding
   *  /role-permissions; the PUT needs the exec/ops tier. */
  canEdit: boolean;
}

/** Per-user page access — the exceptions layered on top of the role matrix.
 *
 *  Ticking a page the role doesn't have stores a GRANT; un-ticking one the
 *  role does have stores a DENIAL. That's why the state is two lists and not
 *  one: clearing both drops the user straight back to their role's defaults,
 *  and a later change to the role matrix still flows through to everyone who
 *  has no exception of their own.
 */
function PageAccessCard({ member, canEdit }: PageAccessCardProps) {
  const [data, setData] = useState<UserPagesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api<UserPagesResponse>(`/api/team/${member.id}/pages`);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled)
          setErr(e instanceof ApiError ? e.message : "Failed to load page access");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [member.id]);

  async function put(grants: string[], denials: string[]) {
    setSaving(true);
    setErr(null);
    try {
      const fresh = await api<UserPagesResponse>(`/api/team/${member.id}/pages`, {
        method: "PUT",
        json: { grants, denials },
      });
      setData(fresh);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to save page access");
      // Re-fetch so the checkboxes never show a state the server rejected.
      try {
        setData(await api<UserPagesResponse>(`/api/team/${member.id}/pages`));
      } catch {
        /* leave the stale view in place if even the read fails */
      }
    } finally {
      setSaving(false);
    }
  }

  function toggle(p: UserPageDef) {
    if (!data || !canEdit || !p.lockable || saving) return;
    const grants = new Set(data.grants);
    const denials = new Set(data.denials);
    if (p.from_role) {
      // Baseline says yes — the only exception available is taking it away.
      if (denials.has(p.key)) denials.delete(p.key);
      else denials.add(p.key);
    } else {
      if (grants.has(p.key)) grants.delete(p.key);
      else grants.add(p.key);
    }
    put(Array.from(grants), Array.from(denials));
  }

  if (err && !data) {
    return (
      <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
        {err}
      </div>
    );
  }
  if (!data) return <Skeleton className="h-48" />;

  const exceptions = data.grants.length + data.denials.length;
  const name = member.full_name || member.email;

  return (
    <div className="card overflow-hidden">
      <SectionHeader
        title="Page access"
        subtitle={
          data.editable
            ? `Starts from what ${roleLabel(member.role)} can see, then adds or removes pages for ${name} alone. Changing the role matrix later still moves everyone who has no exception here.`
            : `${roleLabel(member.role)} sees every page by construction — there is nothing to grant or revoke.`
        }
        right={
          canEdit && data.editable && exceptions > 0 ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                if (confirm(`Clear all page exceptions for ${name} and go back to the ${roleLabel(member.role)} defaults?`))
                  put([], []);
              }}
              className="text-[11.5px] font-medium px-2.5 py-1 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 text-slate-200 transition disabled:opacity-50"
            >
              Reset to role default
            </button>
          ) : null
        }
      />
      <div className="px-5 py-4 space-y-3">
        {err && (
          <div className="text-rose-300 text-xs bg-rose-500/10 border border-rose-500/25 rounded p-2">
            {err}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
          {data.pages.map((p) => {
            const denied = data.denials.includes(p.key);
            const granted = data.grants.includes(p.key);
            const checked = p.from_role ? !denied : granted;
            const disabled = !canEdit || !data.editable || !p.lockable || saving;
            return (
              <label
                key={p.key}
                className={
                  "flex items-center gap-2.5 rounded-md px-2 py-1.5 border text-[12.5px] transition " +
                  (disabled ? "cursor-default " : "cursor-pointer ") +
                  (granted
                    ? "border-emerald-500/35 bg-emerald-500/[0.06]"
                    : denied
                      ? "border-rose-500/35 bg-rose-500/[0.06]"
                      : "border-white/10 bg-white/[0.02] hover:border-white/20")
                }
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded accent-accent-500 disabled:opacity-40"
                  checked={data.editable ? checked : true}
                  disabled={disabled}
                  onChange={() => toggle(p)}
                  aria-label={`${name} can open ${p.label}`}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-slate-200 truncate">{p.label}</span>
                  <span className="block text-[10.5px] text-slate-600 mono truncate">
                    {p.key}
                  </span>
                </span>
                {granted && (
                  <span className="text-[9.5px] uppercase tracking-wider text-emerald-300 font-semibold shrink-0">
                    Granted
                  </span>
                )}
                {denied && (
                  <span className="text-[9.5px] uppercase tracking-wider text-rose-300 font-semibold shrink-0">
                    Blocked
                  </span>
                )}
              </label>
            );
          })}
        </div>
        <div className="text-[11.5px] text-slate-500 leading-snug">
          {!canEdit
            ? "Read-only — only the Executive/Operations Manager tier can change a person's page access."
            : exceptions === 0
              ? `No exceptions — ${name} sees exactly what ${roleLabel(member.role)} sees.`
              : `${exceptions} exception${exceptions === 1 ? "" : "s"} on top of the ${roleLabel(member.role)} role. Takes effect on their next page load.`}
        </div>
      </div>
    </div>
  );
}
